import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import globalDyHandler from 'core/utils/helpers/dyHandler';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import { selectIsMobileOrTabletMQ } from 'core/modules/MediaQueries/selectors';
import MerchantExperienceLink from 'core/modules/MerchantExperienceLink/MerchantExperienceLink';
import MerchantWithOfferCountTile from 'core/modules/MerchantWithOfferCountTile/MerchantWithOfferCountTile';
import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';
import { ShowOnMQ } from 'core/modules/MediaQueries/MediaQueries';
import List from 'core/modules/Partials/List/List';
import { fetchMerchantsByIds } from 'core/actions/ocapi';
import { selectUserIsIdentified } from 'core/selectors/user';
import {
  selectTrendingStoresConfig, selectTrendingStoresIsLoading, selectTrendingStoresIsLoaded,
  selectTrendingStoresRecords, selectTrendingStoresDyData,
} from './selectors';

import { storeTrendingStoresDyData } from './actions';

import './TrendingStores.scss';

export function TrendingStores({ isInverted = false, isPersonalizedMhp }) {
  const dispatch = useDispatch();
  const merchantTileListRef = useRef();
  const config = useSelector(selectTrendingStoresConfig);
  const isLoading = useSelector(selectTrendingStoresIsLoading);
  const isLoaded = useSelector(selectTrendingStoresIsLoaded);
  const merchants = useSelector(selectTrendingStoresRecords);
  const trendingStoresDyData = useSelector(selectTrendingStoresDyData);
  const isUserIdentified = useSelector(selectUserIsIdentified);
  const isMobileOrTabletMQ = useSelector(selectIsMobileOrTabletMQ);

  const {
    feedId, merchantIds, strategyId, widgetId, title,
  } = trendingStoresDyData;

  const passTrendingStoresData = (dyData) => {
    dispatch(storeTrendingStoresDyData(dyData));
  };

  useEffect(() => {
    // Global method that will be triggered from DY campaign
    globalDyHandler.passTrendingStoresData = passTrendingStoresData;
  }, []);

  useEffect(() => {
    if (!isLoaded && !isLoading && !isEmpty(merchantIds)) {
      const { api: { params, options } } = config;
      dispatch(fetchMerchantsByIds(merchantIds, params, options));
    }
  }, [merchantIds]);

  useEffect(() => {
    const merchantListElement = merchantTileListRef.current;
    const dyRegisterElements = window?.DYO?.recommendations?.registerElements;

    if (!isEmpty(merchants) && merchantListElement && dyRegisterElements) {
      // Track the recommendation widget in DY.
      // Syntax: DYO.recommendations.registerElements(el)
      dyRegisterElements(merchantListElement);
    }
  }, [merchants]);

  if (isEmpty(merchants)) {
    return null;
  }
  const { carousel, carouselVhpOverwriting, getContentWrap } = config;
  const carouselConfig = isUserIdentified ? carousel : merge({}, carousel, carouselVhpOverwriting);

  const dataAttributes = {
    'data-dy-widget-id': widgetId,
    'data-dy-feed-id': feedId,
  };

  const contentWrap = getContentWrap(isUserIdentified, isPersonalizedMhp);

  return (
    <div className={`mn_trendingStores ${isInverted ? 'mn_inverted' : ''}`}>
      <ContentWrap {...contentWrap}>
        <SectionTitle title={title || 'Trending Stores'} />
        <List
          options={{ ...carouselConfig, isInverted }}
          shouldUseCarousel={isMobileOrTabletMQ}
          ref={merchantTileListRef}
          dataAttributes={dataAttributes}
          hasFillers={5}
        >
          {merchants.map(merchant => (
            <MerchantExperienceLink
              merchant={merchant}
              key={merchant.id}
              data-dy-product-id={merchant.id}
              data-dy-strategy-id={strategyId}
            >
              <MerchantWithOfferCountTile merchant={merchant} useAddToFavorite={false} />
            </MerchantExperienceLink>
          ))}
        </List>
        <ShowOnMQ mobile><div className="mn_divider" /></ShowOnMQ>
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><TrendingStores {...props} /></ComponentErrorBoundary>;
